import { useCallback, useEffect, useRef, useState } from "react";
import type { AlbumManifest } from "../data/manifest";
import { PageRenderer } from "../render/PageRenderer";
import { CoverArt } from "./CoverArt";
import { Colophon } from "./Colophon";
import { ContributePopover } from "./ContributePopover";
import { inviteCopy } from "../data/invite";
import "./MobileReader.css";

/**
 * Mobile reader (PRD §3 mobile): single page per screen, vertical scroll-snap,
 * neighbours fade, floating 62px bar (page count · join code · contribute),
 * all-pages grid, end colophon. This is the first sequence step; desktop
 * spread + page-flip is the next.
 */
export function MobileReader({
  manifest,
  onCoverChange,
}: {
  manifest: AlbumManifest;
  onCoverChange?: (atCover: boolean) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  // Section 0 = cover, 1..N = pages, N+1 = end colophon.
  const total = manifest.pages.length + 2;
  const [active, setActive] = useState(0);
  const [gridOpen, setGridOpen] = useState(false);
  const [contribOpen, setContribOpen] = useState(
    new URLSearchParams(window.location.search).has("contrib"),
  );

  // Track the most-visible section for the bar + neighbour fade.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            const idx = Number((e.target as HTMLElement).dataset.idx);
            if (!Number.isNaN(idx)) setActive(idx);
          }
        }
      },
      { root, threshold: [0.6] },
    );
    sectionRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [total]);

  // Report "on the cover" (section 0) up to the live-update overlay.
  useEffect(() => {
    onCoverChange?.(active === 0);
  }, [active, onCoverChange]);

  const scrollTo = useCallback((idx: number) => {
    sectionRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setGridOpen(false);
  }, []);

  const setRef = (idx: number) => (el: HTMLElement | null) => {
    sectionRefs.current[idx] = el;
  };

  // active in page range => 1-based page number; else null (cover/end)
  const pageNumber = active >= 1 && active <= manifest.pages.length ? active : null;

  return (
    <div className="m-reader">
      <div className="m-scroll" ref={scrollRef}>
        {/* Cover / landing */}
        <section
          className={`m-section m-cover ${active === 0 ? "is-active" : ""}`}
          data-idx={0}
          ref={setRef(0)}
        >
          <div className="m-page-wrap">
            <CoverArt manifest={manifest} />
          </div>
          {/* Title is baked onto the cover object above; the chrome adds the
              collaborate-invite copy + scroll affordance (Iris). */}
          <div className="m-landing-meta">
            <h1 className="m-invite-head">
              {inviteCopy(manifest.sender.name, "mobile").headline}
            </h1>
            <p className="m-invite-sub">
              {inviteCopy(manifest.sender.name, "mobile").sub}
            </p>
            <p className="m-landing-hint">Scroll to open</p>
            <span
              className={`m-scroll-arrow ${active === 0 ? "" : "is-hidden"}`}
              aria-hidden="true"
            >
              ⌄
            </span>
          </div>
        </section>

        {/* Interior pages */}
        {manifest.pages.map((page, i) => {
          const idx = i + 1;
          const near = Math.abs(idx - active) <= 1;
          return (
            <section
              className={`m-section ${active === idx ? "is-active" : ""}`}
              data-idx={idx}
              ref={setRef(idx)}
              key={page.index}
              aria-label={`Page ${idx} of ${manifest.pageCount}`}
            >
              <div className="m-page-wrap">
                <PageRenderer page={page} photos={manifest.photos} priority={near} />
              </div>
            </section>
          );
        })}

        {/* End */}
        <section
          className={`m-section m-end ${active === total - 1 ? "is-active" : ""}`}
          data-idx={total - 1}
          ref={setRef(total - 1)}
        >
          <Colophon manifest={manifest} onContribute={() => setContribOpen(true)} />
        </section>
      </div>

      {/* Floating bar */}
      <div className="m-bar" role="toolbar" aria-label="Reader controls">
        <button
          type="button"
          className="m-bar__btn"
          onClick={() => setGridOpen((v) => !v)}
          aria-label="All pages"
          aria-expanded={gridOpen}
        >
          <span className="label">
            {pageNumber ? `${pageNumber} / ${manifest.pageCount}` : "Pages"}
          </span>
        </button>
        {/* Join code lives in the contribute sheet, not the always-on bar —
            the code is the album's access key. */}
        <button
          type="button"
          className="m-bar__add"
          aria-label="Add your photos"
          onClick={() => setContribOpen(true)}
        >
          +
        </button>
      </div>

      {/* All-pages grid drawer */}
      {gridOpen && (
        <div className="m-grid" role="dialog" aria-label="All pages">
          <div className="m-grid__scroll">
            {manifest.pages.map((page, i) => {
              const idx = i + 1;
              return (
                <button
                  type="button"
                  key={page.index}
                  className={`m-grid__cell ${active === idx ? "is-current" : ""}`}
                  onClick={() => scrollTo(idx)}
                  aria-label={`Go to page ${idx}`}
                >
                  <PageRenderer page={page} photos={manifest.photos} />
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="m-grid__close"
            onClick={() => setGridOpen(false)}
          >
            Close
          </button>
        </div>
      )}

      <ContributePopover
        open={contribOpen}
        joinCode={manifest.joinCode}
        onClose={() => setContribOpen(false)}
      />
    </div>
  );
}
